// HomePage.js - Updated version based on Adam International Academy documents
import './HomePage.css';
import { Link } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import hero from '../../assets/homepage/hero.jpg';
import tanitimImg from '../../assets/homepage/tanitim.jpg';
import farkImg from '../../assets/homepage/fark.jpg';
import programImg from '../../assets/homepage/program.jpg';
import mezunImg from '../../assets/homepage/mezun.jpg';
import mezun2Img from '../../assets/homepage/mezun2.jpg';
import mezun3Img from '../../assets/homepage/mezun3.jpg';
import isbirligiImg from '../../assets/homepage/isbirligi.jpg';
import TurkishAirlines from '../../assets/homepage/turkish_airlines.png';

// Import partner logos - GÜNCELLENDİ
import ABCHorizon from '../../assets/partners/abc-horizon.png';
import StudyFans from '../../assets/partners/studyfans.png';
import GHEducation from '../../assets/partners/gh-education.png';
import AdamHoldingUnion from '../../assets/partners/adam-holding-union.png';
import ScopeAcademy from '../../assets/partners/scope-academy.png';
import Target from '../../assets/partners/Target.png';
import Afrecom from '../../assets/partners/Afrecom.jpg';
import Ecopa from '../../assets/partners/Ecopa.jpg';
import Medipol from '../../assets/partners/medipol.png';
import SabahattinZaim from '../../assets/partners/sabahattin-zaim.png';
import AITU from '../../assets/partners/aitu.png';
import AdamBank from '../../assets/partners/adam-bank.png';
import AdamGlobalFoundation from '../../assets/partners/adam-global-foundation.png';
import SAK from '../../assets/partners/sak.png';

function HomePage() {
  // State for counters - Updated with targets from documents
  const [counts, setCounts] = useState({ 
    projects: 0, 
    courses: 0, 
    students: 0, 
    faculty: 0,
    countries: 0,
    partners: 0 
  });
  const [isCounting, setIsCounting] = useState(false);
  const statsRef = useRef(null);
  const [activeSection, setActiveSection] = useState(null);

  // Intersection Observer to trigger counters and animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target.classList.contains('hp-stats') && !isCounting) {
              setIsCounting(true);
            }
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px' }
    );

    const sections = document.querySelectorAll('.hp-section');
    sections.forEach(section => {
      if (section) observer.observe(section);
    });

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      sections.forEach(section => {
        if (section) observer.unobserve(section);
      });
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [isCounting]);

  // Counter animation - Updated with realistic targets from documents
  useEffect(() => {
    if (!isCounting) return;

    const targets = { 
      projects: 150,          // Completed projects
      courses: 45,            // Courses offered (eLearning + face-to-face)
      students: 2500,         // Students (growing academy)
      faculty: 100,           // Faculty members (~100 from documents)
      countries: 30,          // Operating in 30+ countries
      partners: 50            // Strategic partner organizations
    };
    
    let interval;
    
    const startCounting = () => {
      interval = setInterval(() => {
        setCounts((prev) => {
          const newCounts = { ...prev };
          let completed = true;
          
          Object.keys(targets).forEach((key) => {
            if (prev[key] < targets[key]) {
              const increment = Math.ceil((targets[key] - prev[key]) / 20);
              newCounts[key] = Math.min(prev[key] + (increment || 1), targets[key]);
              completed = false;
            }
          });
          
          if (completed) clearInterval(interval);
          return newCounts;
        });
      }, 30);
    };

    startCounting();

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isCounting]);

  // Program carousel - Updated with eLearning formats from documents
  const [programIndex, setProgramIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef(null);
  
  const programs = [
    { 
      id: 1, 
      title: "📋 Self-Paced eLearning", 
      text: "30+ interactive online courses | Self-paced learning | 24/7 access | Certificate opportunity | SDG 2030 aligned content" 
    },
    { 
      id: 2, 
      title: "💻 Blended Learning", 
      text: "Programs combining online and face-to-face training | Practical workshops | Case studies | Mentorship support" 
    },
    { 
      id: 3, 
      title: "🌍 Massive Open Online Courses (MOOCs)", 
      text: "Free courses offered through global collaborations | 1000+ participants | Multilingual content | Academic credit opportunity" 
    },
    { 
      id: 4, 
      title: "🎓 University Master's & Postgraduate", 
      text: "Accredited graduate programs with partner universities | Political Science, International Relations, Strategic Studies, International Law" 
    },
    { 
      id: 5, 
      title: "📱 Mobile Learning", 
      text: "Micro-learning modules accessible from mobile devices | Flexible learning | Instant feedback" 
    },
    { 
      id: 6, 
      title: "🔧 Technical Webinars", 
      text: "Live technical webinars with experts | Sectoral updates | Q&A sessions | Recorded access" 
    },
    { 
      id: 7, 
      title: "🏛 Face-to-Face Training", 
      text: "In-person training programs in Turkey, Bahrain and Africa | Intensive workshops | Networking events" 
    },
    { 
      id: 8, 
      title: "📊 Professional Diplomas", 
      text: "60-300 hour professional diploma programs | Industry-approved curriculum | International validity" 
    }
  ];

  // Auto-scroll for carousel
  useEffect(() => {
    if (isPaused) return;
    
    const getCardsPerView = () => {
      if (window.innerWidth < 768) return 1;
      if (window.innerWidth < 1200) return 2;
      return 3;
    };

    const interval = setInterval(() => {
      setProgramIndex((prev) => {
        const cardsPerView = getCardsPerView();
        const maxIndex = Math.max(0, programs.length - cardsPerView);
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 5000);
    
    return () => clearInterval(interval);
  }, [programs.length, isPaused]);

  const nextProgram = useCallback(() => {
    setProgramIndex((prevIndex) => {
      const cardsPerView = window.innerWidth < 768 ? 1 : window.innerWidth < 1200 ? 2 : 3;
      const maxIndex = Math.max(0, programs.length - cardsPerView);
      return prevIndex >= maxIndex ? 0 : prevIndex + 1;
    });
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 8000);
  }, [programs.length]);

  const prevProgram = useCallback(() => {
    setProgramIndex((prevIndex) => {
      const cardsPerView = window.innerWidth < 768 ? 1 : window.innerWidth < 1200 ? 2 : 3;
      const maxIndex = Math.max(0, programs.length - cardsPerView);
      return prevIndex <= 0 ? maxIndex : prevIndex - 1;
    });
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 8000);
  }, [programs.length]);

  // Logos - GÜNCELLENDİ: Verdiğiniz tüm partner logoları eklendi
  const logos = [
    { name: "ABC Horizon University", img: ABCHorizon },
    { name: "StudyFans", img: StudyFans },
    { name: "GH Education", img: GHEducation },
    { name: "Adam Holding Union", img: AdamHoldingUnion },
    { name: "Scope Academy", img: ScopeAcademy },
    { name: "Target", img: Target },
    { name: "Afrecom", img: Afrecom },
    { name: "Ecopa", img: Ecopa },
    { name: "Medipol University", img: Medipol },
    { name: "Sabahattin Zaim University", img: SabahattinZaim },
    { name: "American International Theism University", img: AITU },
    { name: "Adam Bank", img: AdamBank },
    { name: "Adam Global Foundation", img: AdamGlobalFoundation },
    { name: "SAK", img: SAK }
  ];

  const duplicatedLogos = [...logos, ...logos, ...logos]; // 3 katına çıkararak sürekli kayma için yeterli

  // Faculty specialties - From documents
  const facultySpecialties = [
    "(SDGs)", "Multilingual Education", "Innovation", 
    "Capacity Development", "(PI)", "International Relations",
    "Digital Transformation", "(AI)", "Data Science", 
    "Cybersecurity", "Project Management", "Six Sigma", "Change Management", "Leadership Development",  "Strategic Planning","Value Engineering", 
  ];

  // Partner categories - From AIA Partners document
  const partnerCategories = [
    "United Nations Agencies", "Development Agencies", 
    "International Organizations", "Private Sector", 
    "Academic Institutions", "Research Centers", 
    "Civil Society Organizations (CSOs)", "NGOs"
  ];

  // Smooth scroll
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="hp-wrapper">
      {/* HERO - Updated with official name from documents */}
      <header className="hp-hero" style={{ backgroundImage: `url(${hero})` }}>
        <div className="hp-hero-overlay">
          <div className="hp-hero-content">
            <h1 className="hp-hero-title animate-fade-in">ADAM INTERNATIONAL ACADEMY (AIA)</h1>
            <h2 className="hp-hero-subtitle animate-fade-in-delay">Of Science, Technology and Development</h2>
            <p className="hp-hero-desc animate-fade-in-delay-2">
              Without Borders, Achieve With Excellence. Education is more than knowledge — it is a bridge to global opportunity. 
              Aligned with SDG 2030, accredited, connected, and global.
            </p>
            <p className="hp-hero-desc animate-fade-in-delay-2">
              A global educational institution specialized in capacity development, innovation, and multilingual education.
            </p>
            <div className="hp-hero-buttons animate-fade-in-delay-3">
              <Link to="/programs" className="hp-btn primary">📋 Explore Programs</Link>
              <Link to="/apply" className="hp-btn secondary">📌 Apply Now</Link>
            </div>
          </div>
        </div>
      </header>

      {/* STATS COUNTERS - Updated */}
      <section className="hp-section hp-stats" ref={statsRef} id="stats">
        <div className="hp-container">
          <div className="hp-stats-grid" style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}>
            <div className="hp-stat-item">
              <h3>{counts.projects}</h3>
              <p>Completed Projects</p>
            </div>
            <div className="hp-stat-item">
              <h3>{counts.courses}</h3>
              <p>Courses Offered</p>
            </div>
            <div className="hp-stat-item">
              <h3>{counts.students}+</h3>
              <p>Students</p>
            </div>
            <div className="hp-stat-item">
              <h3>{counts.faculty}</h3>
              <p>Faculty Members</p>
            </div>
            <div className="hp-stat-item">
              <h3>{counts.countries}</h3>
              <p>Countries</p>
            </div>
            <div className="hp-stat-item">
              <h3>{counts.partners}</h3>
              <p>Strategic Partners</p>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT - Updated with mission from documents */}
      <section className="hp-section" id="about">
        <div className="hp-container hp-section-content">
          <div className="hp-section-text">
            <h2 className="hp-section-title">🏛 About Adam International Academy</h2>
            <p>
              <strong>Adam International Academy of Science, Technology and Development</strong> is a global educational 
              and training institution specialized in applied sciences, humanities and social studies.
            </p>
            <p>
              Our mission is to strengthen the capacity of members to achieve the 2030 Agenda for Sustainable Development 
              (SDG 2030). We focus on the transfer of competencies, development of expertise and promotion of innovation.
            </p>
            <p>
              <strong>Our Target Audience:</strong> Policymakers, government officials, technical staff, program and 
              project designers, and professionals supporting SDG 2030 implementation.
            </p>
          </div>
          <img src={tanitimImg} alt="Adam Academy" className="hp-section-image animate-on-scroll" loading="lazy" />
        </div>
      </section>

      {/* SDG HIGHLIGHT - New Section */}
      <section className="hp-section hp-sdg-section">
        <div className="hp-container">
          <h2 className="hp-section-title">🌍 Alignment with Sustainable Development Goals</h2>
          <div className="hp-sdg-content">
            <p>
              Our courses are closely aligned with the Sustainable Development Goals (SDGs) and Adam strategic programs, 
              and are peer reviewed by a wide range of experts to ensure content accuracy, quality and coherence.
            </p>
            <p className="hp-sdg-highlight">
              <strong>Especially SDG 4:</strong> "Ensure inclusive and equitable quality education and promote lifelong 
              learning opportunities for all."
            </p>
          </div>
        </div>
      </section>

      {/* THE ADAM DIFFERENCE */}
      <section className="hp-section hp-features-section" id="difference">
        <div className="hp-container hp-section-content reverse">
          <div className="hp-section-text">
            <h2 className="hp-section-title">✨ The Adam Difference</h2>
            <h3 className="hp-subtitle">Pedagogical Models and Delivery Mechanisms</h3>
            <ul className="hp-feature-list">
              <li>📱 Mobile Learning</li>
              <li>💻 Self-paced eLearning</li>
              <li>🎯 Blended Learning</li>
              <li>🌍 Massive Open Online Courses (MOOCs)</li>
              <li>🔧 Technical Webinars</li>
              <li>👨‍🏫 Online Tutored Courses</li>
              <li>🏛 Face-to-Face Training Workshops</li>
              <li>🎓 University Master's & Postgraduate Programs</li>
            </ul>
            
            <h3 className="hp-subtitle">Faculty Specialties</h3>
            <div className="hp-specialties-grid">
              {facultySpecialties.map((specialty, index) => (
                <span key={index} className="hp-specialty-tag">{specialty}</span>
              ))}
            </div>
          </div>
          <img src={farkImg} alt="The Adam Difference" className="hp-section-image animate-on-scroll" loading="lazy" />
        </div>
      </section>

      {/* PROGRAM CAROUSEL - Updated */}
      <section className="hp-section hp-program-section" id="programs">
        <div className="hp-container">
          <h2 className="hp-section-title hp-section-title-light">📚 Educational Programs</h2>
          
          <div className="hp-program-carousel-container" ref={carouselRef}>
            <div 
              className="hp-program-carousel-track" 
              style={{ 
                transform: `translateX(-${programIndex * (100 / (window.innerWidth < 768 ? 1 : window.innerWidth < 1200 ? 2 : 3))}%)`,
              }}
            >
              {programs.map((program) => (
                <div key={program.id} className="hp-program-card">
                  <h3>{program.title}</h3>
                  <p>{program.text}</p>
                </div>
              ))}
            </div>
            
            <div className="hp-program-nav">
              <button onClick={prevProgram} aria-label="Previous program">←</button>
              <button onClick={nextProgram} aria-label="Next program">→</button>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERSHIPS - From AIA Partners document */}
      <section className="hp-section" id="partners">
        <div className="hp-container">
          <h2 className="hp-section-title">🤝 Strategic Partnerships</h2>
          <p className="hp-partner-intro">
            To achieve our capacity development goals, we collaborate with the following organizations:
          </p>
          
          <div className="hp-partner-categories">
            {partnerCategories.map((category, index) => (
              <div key={index} className="hp-partner-category">{category}</div>
            ))}
          </div>
          
          <h3 className="hp-subtitle" style={{ marginTop: '3rem' }}>University Partners</h3>
          <div className="hp-partner-universities">
            <ul>
              <li>Sabahattin Zaim University (Turkey)</li>
              <li>Medipol University (Turkey)</li>
              <li>American International Theism University</li>
              <li>ABC Horizon University (Turkey - UK)</li>
            </ul>
          </div>
          
          <div className="hp-logo-slider" style={{ marginTop: '2rem' }}>
            <div className="hp-logo-slider-track">
              {duplicatedLogos.map((logo, index) => (
                <div key={index} className="hp-logo-slide">
                  <img src={logo.img} alt={logo.name} loading="lazy" />
                  <p>{logo.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COLLABORATION MODELS - New Section */}
      <section className="hp-section hp-collaboration">
        <div className="hp-container">
          <h2 className="hp-section-title">🔄 Our Collaboration Models</h2>
          <div className="hp-collab-grid">
            <div className="hp-collab-item">
              <h3>📝 Curriculum Design</h3>
              <p>Comprehensive curriculum design through multi-stakeholder and multi-disciplinary learning needs assessments.</p>
            </div>
            <div className="hp-collab-item">
              <h3>👥 Content Development</h3>
              <p>Course content development with subject experts from partner organizations, university professors and Adam technical units.</p>
            </div>
            <div className="hp-collab-item">
              <h3>🌐 Multilingual Adaptation</h3>
              <p>Adaptation of courses to different language environments in collaboration with partner institutions.</p>
            </div>
            <div className="hp-collab-item">
              <h3>📢 Dissemination</h3>
              <p>Sharing capacity development materials with UN agencies, development agencies, CSOs and NGOs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="hp-section hp-testimonials" id="testimonials">
        <div className="hp-container">
          <h2 className="hp-section-title">🏆 Success Stories</h2>
          <div className="hp-testimonial-grid">
            <div className="hp-testimonial-card">
              <img src={mezunImg} alt="Alumni" className="hp-circle-img" loading="lazy" />
              <p>"The education at Adam International Academy enabled me to lead sustainable development projects in my country aligned with SDG goals."</p>
              <span>- Development Agency Specialist, Africa</span>
            </div>
            <div className="hp-testimonial-card">
              <img src={mezun2Img} alt="Alumni" className="hp-circle-img" loading="lazy" />
              <p>"Thanks to the online education model, I completed my master's while working full-time. The mentorship support was excellent."</p>
              <span>- Ayşe K., Project Manager</span>
            </div>
            <div className="hp-testimonial-card">
              <img src={mezun3Img} alt="Alumni" className="hp-circle-img" loading="lazy" />
              <p>"The multilingual education opportunity and international perspective opened new doors in my career."</p>
              <span>- Mehmet T., Diplomat</span>
            </div>
          </div>
        </div>
      </section>

      {/* TARGET AUDIENCE */}
      <section className="hp-section hp-target-audience">
        <div className="hp-container">
          <h2 className="hp-section-title">👥 Our Target Audience</h2>
          <div className="hp-audience-grid">
            <div className="hp-audience-item">Policymakers</div>
            <div className="hp-audience-item">Government Officials</div>
            <div className="hp-audience-item">Technical Staff</div>
            <div className="hp-audience-item">Program Designers</div>
            <div className="hp-audience-item">Project Managers</div>
            <div className="hp-audience-item">SDG Professionals</div>
            <div className="hp-audience-item">Civil Society Leaders</div>
            <div className="hp-audience-item">Private Sector Representatives</div>
          </div>
        </div>
      </section>

      {/* APPLICATION CTA */}
      <section className="hp-section hp-apply-cta" id="apply">
        <div className="hp-container">
          <h2>Ready to Become a Game Changer?</h2>
          <p>Join the leaders contributing to SDG 2030 goals</p>
          <div className="hp-apply-buttons">
            <Link to="/apply" className="hp-btn primary">Apply Now</Link>
            <Link to="/contact" className="hp-btn secondary">Contact Us</Link>
          </div>
          <div className="hp-contact-info">
            <p>📞 (+90) 380-772631</p>
            <p>✉️ info@adaminternationalacademy.com</p>
            <p>🌍 www.adaminternationalacademy.com</p>
            <p>📍 Manama, Bahrain | Istanbul, Turkey</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;